/* eslint-disable react/prop-types */
import React from 'react';
import { Autocomplete, TextField, Box, Button, Typography } from '@mui/material';

const TEAL = '#00798E';

/**
 * Keyboard-first searchable dropdown that can also create a new option
 * inline. Sibling of <SearchableSelect> — use this when the user should be
 * able to add a value that isn't in the list yet (Tally-style).
 *
 * Props:
 *   options       : [{ value, label }]  or  [string]
 *   value         : current value (matches option.value)
 *   onChange      : (value) => void
 *   onCreateNew   : (typedText) => void   — opens the caller's create flow
 *   label, placeholder, disabled, size ('small'|'medium'), fullWidth
 *   inputRef      : forwarded to the text input (for focus management)
 *   onKeyDownLast : extra keydown handler on the input (e.g. Enter → next field)
 *   createLabel   : verb shown in the create row (default "Create")
 */
export default function CreatableSelect({
  options = [],
  value,
  onChange,
  onCreateNew,
  label,
  placeholder,
  disabled,
  size = 'small',
  fullWidth = true,
  inputRef,
  onKeyDownLast,
  createLabel = 'Create',
}) {
  const normalized = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o,
  );
  const selected =
    normalized.find((o) => String(o.value) === String(value ?? '')) || null;

  const filterOptions = (opts, state) => {
    const q = (state.inputValue || '').trim().toLowerCase();
    const filtered = q
      ? opts.filter((o) => (o.label || '').toLowerCase().includes(q))
      : [...opts];
    if (onCreateNew) {
      filtered.push({
        __create: true,
        value: '__create__',
        label: q ? `${createLabel} "${state.inputValue}"` : `${createLabel} new…`,
        _typed: state.inputValue || '',
      });
    }
    return filtered;
  };

  return (
    <Autocomplete
      options={normalized}
      value={selected}
      disabled={disabled}
      fullWidth={fullWidth}
      autoHighlight
      openOnFocus
      selectOnFocus
      handleHomeEndKeys
      getOptionLabel={(o) => o?.label || ''}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v?.value ?? '')}
      filterOptions={filterOptions}
      onChange={(_, newVal) => {
        if (newVal && newVal.__create) {
          onCreateNew && onCreateNew(newVal._typed || '');
          return;
        }
        onChange(newVal ? newVal.value : '');
      }}
      componentsProps={{ popper: { sx: { zIndex: 9999 } } }}
      renderOption={(props, o) => (
        <Box
          component="li"
          {...props}
          key={o.__create ? '__create' : o.value}
          sx={
            o.__create
              ? { color: TEAL, fontWeight: 700, fontSize: '0.8rem', borderTop: '1px solid #eee' }
              : { fontSize: '0.8rem' }
          }
        >
          {o.__create ? `➕ ${o.label}` : o.label}
        </Box>
      )}
      noOptionsText={
        onCreateNew ? (
          <Box sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#888', mb: 0.5 }}>
              Nothing found
            </Typography>
            <Button
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onCreateNew('')}
              sx={{ color: TEAL, fontSize: '0.75rem', fontWeight: 700, p: 0, '&:hover': { background: 'transparent' } }}
            >
              ➕ {createLabel} new
            </Button>
          </Box>
        ) : (
          'Nothing found'
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={placeholder}
          inputRef={inputRef}
          onKeyDown={onKeyDownLast}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: size === 'small' ? 34 : 44,
              fontSize: '0.82rem',
              '&.Mui-focused fieldset': { borderColor: TEAL },
            },
          }}
        />
      )}
    />
  );
}
